import React, { useEffect, useState, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { useEngagementTracking } from "./hooks/useEngagementTracking";
import { trackPageView } from "./utils/analytics";

// Eager load Home for best LCP (it's the landing page)
import Home from "./pages/Home";

// Lazy load all other pages for code splitting
const MadinahPortfolio = lazy(() => import("./pages/MadinahPortfolio"));
const AlMajdiehVillageVillas = lazy(() => import("./pages/properties/AlMajdiehVillageVillas"));
const AlMajdiehVillageApartments = lazy(() => import("./pages/properties/AlMajdiehVillageApartments"));
const AlMajdiehComplex = lazy(() => import("./pages/properties/AlMajdiehComplex"));
const KECAliyahComplex = lazy(() => import("./pages/properties/KECAliyahComplex"));
const MovingToMadinah = lazy(() => import("./pages/MovingToMadinah"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const Visas = lazy(() => import("./pages/Visas"));
const Relations = lazy(() => import("./pages/Relations"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const Blogs = lazy(() => import("./pages/Blogs"));
const FAQ = lazy(() => import("./pages/FAQ"));
const AboutDeveloper = lazy(() => import("./pages/AboutDeveloper"));
const PaymentPlan = lazy(() => import("./pages/PaymentPlan"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const NotFound = lazy(() => import("./pages/NotFound"));

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AnalyticsTracker() {
  const { pathname } = useLocation();

  useEngagementTracking();

  useEffect(() => {
    trackPageView(pathname, document.title);
  }, [pathname]);

  return null;
}

function AppContent() {
  const [isHeroSection, setIsHeroSection] = useState(true);
  const { pathname } = useLocation();
  const routesWithoutNavbarPadding = new Set([
    "/",
    "/properties/al-majdieh-village-villas",
    "/properties/al-majdieh-village-apartments",
  ]);

  useEffect(() => {
    // Only apply scroll detection on home page
    if (pathname !== "/") {
      setIsHeroSection(false);
      return;
    }

    // Initialize state for home page
    setIsHeroSection(true);

    const handleScroll = () => {
      // Check if we're still in the hero section (first 100vh)
      const heroHeight = window.innerHeight;
      setIsHeroSection(window.scrollY < heroHeight * 0.1);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname]);

  return (
    <div className="App">
      <ScrollToTop />
      <AnalyticsTracker />
      <Navbar isHeroSection={isHeroSection} isHomePage={pathname === "/"} />
      <main
        className={`main-content ${
          routesWithoutNavbarPadding.has(pathname)
            ? ""
            : "main-content-with-navbar-padding"
        }`}
      >
        <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/madinah-portfolio" element={<MadinahPortfolio />} />
            <Route
              path="/properties/al-majdieh-village-villas"
              element={<AlMajdiehVillageVillas />}
            />
            <Route
              path="/properties/al-majdieh-village-apartments"
              element={<AlMajdiehVillageApartments />}
            />
            <Route
              path="/properties/al-majdieh-complex"
              element={<AlMajdiehComplex />}
            />
            <Route
              path="/properties/kec-aliyah-complex"
              element={<KECAliyahComplex />}
            />
            <Route path="/moving-to-madinah" element={<MovingToMadinah />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/about-developer" element={<AboutDeveloper />} />
            <Route path="/payment-plan" element={<PaymentPlan />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/visas" element={<Visas />} />
            <Route path="/relations" element={<Relations />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/faq" element={<FAQ />} />
            <Route
              path="/terms-and-conditions"
              element={<TermsAndConditions />}
            />
            <Route
              path="/may-furniture-offer"
              element={<Navigate to="/" replace />}
            />
            <Route
              path="/april-furniture-offer"
              element={<Navigate to="/" replace />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
